// C6 (REF01-M 3.18–3.30): the HospitalVisitation texts of a viderevisitering.
// Mirrors Communicate's api/src/ref01/revisit.ts so the counters in the form
// match what is built: headings from the qualifier list, lines of 70
// characters, max 5 lines/350 characters per element, 9 elements, 3150 in all.

export const REVISIT_CODES = [
  'visitering',
  'indkaldt',
  'indlagt_paa',
  'udenamts_kaution',
  'egen_laege',
  'ustruktureret',
] as const;
export type RevisitCode = (typeof REVISIT_CODES)[number];

/** All 7 InformationCodeType values (TEXTNR), in the order they are offered. */
export const ALL_VISITATION_CODES = ['henvisning_modtaget', ...REVISIT_CODES] as const;

export const REVISIT_TOTAL_MAX = 3150;
export const REVISIT_MAX_ELEMENTS = 9;
export const REVISIT_LINE_WIDTH = 70;
export const REVISIT_LINES_PER_ELEMENT = 5;

export const REVISIT_HEADINGS: Record<(typeof ALL_VISITATION_CODES)[number], string | null> = {
  henvisning_modtaget: 'HENVISNING MODTAGET:',
  visitering: 'VISITERING:',
  indkaldt: 'INDKALDT:',
  indlagt_paa: 'INDLAGT PÅ:',
  udenamts_kaution: 'UDENAMTSHENVISNING:',
  egen_laege: 'EGEN LÆGE:',
  ustruktureret: null,
};

export function isRevisitCode(value: string): value is RevisitCode {
  return (REVISIT_CODES as readonly string[]).includes(value);
}

export function wrapRevisitLines(text: string, width = REVISIT_LINE_WIDTH): string[] {
  const out: string[] = [];
  for (const paragraph of text.replace(/\r\n?/g, '\n').split('\n')) {
    const words = paragraph.trim().split(/\s+/).filter((w) => w !== '');
    if (words.length === 0) {
      out.push('');
      continue;
    }
    let line = '';
    for (let word of words) {
      while (word.length > width) {
        if (line) {
          out.push(line);
          line = '';
        }
        out.push(word.slice(0, width));
        word = word.slice(width);
      }
      if (!line) line = word;
      else if (line.length + 1 + word.length <= width) line = `${line} ${word}`;
      else {
        out.push(line);
        line = word;
      }
    }
    if (line) out.push(line);
  }
  while (out.length > 0 && out[out.length - 1] === '') out.pop();
  while (out.length > 0 && out[0] === '') out.shift();
  return out;
}

export type RevisitTextRow = { code: RevisitCode; text: string };

/** Lines and HospitalVisitation elements one text becomes, heading included. */
export function revisitTextShape(row: RevisitTextRow): { lines: string[]; elements: number; characters: number } {
  const body = wrapRevisitLines(row.text);
  if (body.length === 0) return { lines: [], elements: 0, characters: 0 };
  const heading = REVISIT_HEADINGS[row.code];
  const lines = heading ? [heading, ...body] : body;
  return {
    lines,
    elements: Math.ceil(lines.length / REVISIT_LINES_PER_ELEMENT),
    characters: lines.join('').length,
  };
}

export type RevisitBudget = {
  elements: number;
  characters: number;
  emptyRows: number;
  overElements: boolean;
  overCharacters: boolean;
  ok: boolean;
};

/**
 * Totals for the whole HospitalVisitation field. `receivedLines` is the
 * henvisning_modtaget element that always comes first (kept or created).
 */
export function revisitBudget(rows: readonly RevisitTextRow[], receivedLines: readonly string[]): RevisitBudget {
  let elements = 1;
  let characters = receivedLines.join('').length;
  let emptyRows = 0;
  for (const row of rows) {
    const shape = revisitTextShape(row);
    if (shape.elements === 0) emptyRows += 1;
    elements += shape.elements;
    characters += shape.characters;
  }
  const overElements = elements > REVISIT_MAX_ELEMENTS;
  const overCharacters = characters > REVISIT_TOTAL_MAX;
  return {
    elements,
    characters,
    emptyRows,
    overElements,
    overCharacters,
    ok: rows.length > 0 && emptyRows === 0 && !overElements && !overCharacters,
  };
}

/** DD.MM.YYYY in Danish time, as written after HENVISNING MODTAGET. */
export function danishReceivedDate(iso: string): string {
  const parts = new Intl.DateTimeFormat('da-DK', {
    timeZone: 'Europe/Copenhagen',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')}`;
}

/**
 * The henvisning_modtaget element of the received referral, or the one that
 * will be created from the date the clinic received it.
 */
export function receivedVisitationLines(
  hospitalVisitations: unknown,
  receivedAt: string,
): { lines: string[]; kept: boolean } {
  if (Array.isArray(hospitalVisitations)) {
    for (const item of hospitalVisitations) {
      if (
        item &&
        typeof item === 'object' &&
        (item as Record<string, unknown>).informationCode === 'henvisning_modtaget'
      ) {
        const info = (item as Record<string, unknown>).information;
        const lines = Array.isArray(info) ? info.filter((l): l is string => typeof l === 'string' && l !== '') : [];
        return { lines, kept: true };
      }
    }
  }
  return { lines: ['HENVISNING MODTAGET:', danishReceivedDate(receivedAt)], kept: false };
}
